





























































































































































































































































import { Component, Mixins, Prop } from "vue-property-decorator";
import { DataModules } from "@/store/modules/data";
import { IAppliedTax } from "@/models/tax";
import { IInvoice, InvoiceCategoryCode } from "@/models/invoices";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";
import Context from "@/mixins/context";
import _ from "@/boot/lodash";
import { ClientRoutes } from "@/data/enums/router";

@Component({
  components: {
    "invoice-paid-stamp": () =>
      import("@/components/app/global/invoices/invoicePaidStamp.vue")
  }
})
export default class InvoiceItems extends Mixins(BillingCyclesStore, Context) {
  // Props

  @Prop({ required: true })
  public invoice!: IInvoice;

  @Prop({ default: false })
  public readOnly!: boolean;

  @Prop({ default: true })
  public showAmounts!: boolean;

  // Computed

  public get isLoading() {
    return _.isEmpty(this.invoice);
  }

  public get isMigrationInvoice() {
    return (
      _.get(this.invoice, "category.slug") === InvoiceCategoryCode.MIGRATION
    );
  }

  public get isOverdue() {
    return this.$store.getters[`data/${DataModules.INVOICES}/isOverdue`](
      this.invoice
    );
  }

  public get isPaid() {
    return this.$store.getters[`data/${DataModules.INVOICES}/isPaid`](
      this.invoice
    );
  }

  public get isCreditNote() {
    return this.$store.getters[`data/${DataModules.INVOICES}/isCreditNote`](
      this.invoice
    );
  }

  public get mappedProductImages() {
    return _.mapValues(
      _.keyBy(
        _.get(this.invoice, "products", []),
        product => product.product.id
      ),
      "product.image.full_url"
    );
  }

  public get invoiceItems() {
    const items: any[] = [];
    _.forEach(
      _.get(
        this.invoice,
        "current_data.content.products",
        _.get(this.invoice, "products", [])
      ),
      product => {
        const item = {
          id: product.id,
          brandId: product.product.brand_id,
          name: product.product.name_translated || product.product.name,
          category:
            product.product.category.name_translated ||
            product.product.category.name,
          period:
            product.from_date && product.to_date
              ? `${this.$moment(product.from_date).format(
                  this.$t("_datetime.date.short") as string
                )} - ${this.$moment(product.to_date).format(
                  this.$t("_datetime.date.short") as string
                )}`
              : null,
          quantity: product.quantity,
          total: product.configuration_net_amount_formatted,
          sellingPrice: product.configuration_net_selling_price_formatted,
          qtyBasedSubItems: [] as any[],
          nonQtyBasedSubItems: [] as any[],
          isSubscription: !!product.billing_cycle_days,
          billingCycle: this.getBillingCycleName(product.billing_cycle_months),
          image: _.get(
            product,
            "product.image.full_url",
            _.get(this.mappedProductImages, product.product.id)
          ),
          route: {
            to: {
              name: this.isAdmin
                ? "admin__clientCProd"
                : this.isMockClientContext
                ? "admin__upmindClientCProd"
                : ClientRoutes.CONTRACT_PRODUCT,
              params: {
                clientId: this.invoice.client_id,
                productId: product.contracts_product_id,
                contractId: this.invoice.contract_id
              }
            }
          },
          product: product
        };

        // Add options
        _.forEach(product.options, option => {
          const subProduct = _.get(option, "product");
          item[
            option.quantity === product.quantity
              ? "qtyBasedSubItems"
              : "nonQtyBasedSubItems"
          ].push({
            id: option.id,
            name: `${subProduct.category.name_translated}: ${subProduct.name_translated}`,
            quantity: option.quantity,
            unit_quantity: _.get(option, "unit_quantity", 1),
            total: option.net_selling_price_formatted,
            sellingPrice: option.net_unit_selling_price_formatted,
            subItem: true,
            isOption: true
          });
        });

        // Add attributes
        _.forEach(product.attributes, attribute => {
          const subProduct = _.get(attribute, "product");
          item.qtyBasedSubItems.push({
            id: attribute.id,
            name: `${subProduct.category.name_translated}: ${subProduct.name_translated}`,
            quantity: attribute.quantity,
            unit_quantity: _.get(attribute, "unit_quantity", 1),
            total: "",
            subItem: true,
            isAttribute: true
          });
        });

        // Add product discounts
        const productPromotions = _.get(product, "promotions", []);
        const appliedPromotions = (
          _.get(
            this.invoice,
            "current_data.content.promotions",
            _.get(this.invoice, "promotions")
          ) || []
        )
          .map(p => {
            const productPromotion = productPromotions.find(
              pp => pp.promotion_id === p.promotion.id && !p.promotion.global
            );
            return {
              ...p,
              productPromotion
            };
          })
          .filter(p => !!p.productPromotion);

        _.forEach(appliedPromotions, promotion => {
          item.nonQtyBasedSubItems.push({
            id: promotion.id,
            name: `${this.$t("_.promotion")}: ${promotion.promotion.code}`,
            total: `${promotion.productPromotion.net_amount_formatted}`,
            subItem: true
          });
        });

        if (item.qtyBasedSubItems.length || item.nonQtyBasedSubItems.length) {
          item.qtyBasedSubItems.unshift({
            id: `sub-${product.id}`,
            name: product.product_name,
            quantity: product.quantity,
            total: product.configuration_net_amount_formatted,
            sellingPrice: product.net_unit_selling_price_formatted,
            subItem: true
          });
        }

        items.push(item);
      }
    );

    return items;
  }

  public get invoicePromotions() {
    return this.$store.getters[`data/${DataModules.INVOICES}/mappedPromotions`](
      this.invoice
    );
  }

  public get tfootSlotRowspan() {
    return (
      5 +
      (this.invoice.partial_amount_credited_converted ? 1 : 0) +
      (_.get(this.invoice, "net_global_discount_amount") ? 1 : 0) +
      (this.isCreditNote ? 0 : 2) +
      (this.invoice?.taxes?.length || 0)
    );
  }

  public getProductName(item) {
    return this.$_.compact([
      item.name,
      this.$_.get(item, "product.service_identifier")
        ? `(${this.$_.get(item, "product.service_identifier")})`
        : null
    ]).join(" ");
  }

  public getAppliedTaxTagName(tax: IAppliedTax) {
    return this.$store.getters[
      `data/${DataModules.TAX_TAGS}/computeAppliedTagName`
    ](tax.tax_tag_data?.[0]);
  }
}
